import { createAsyncThunk, createSlice, current, isDraft, PayloadAction } from "@reduxjs/toolkit"
import { TZyomuinGetRes } from "../../../service/driver/TZyomuin"
import ZyomuinService from "../../../service/driver/zyomuinService"
import commonUtil from "../../../ts/util/commonUtil"

// immutable
import { copyObject } from "@grapecity/wijmo.grid.immutable"

import {
  TZyomuinMasterBase,
  TMenkyoJoho,
  initialMenkyoJohoState,
  initialSyakenSyoState,
  TPictureFile,
} from "./baseData"
import { initialBsyoIdoRirekiDataTable, TBsyoIdoRirekiData, TBsyoIdoRirekiDataTable } from "./busyoIdoRireki"
import {
  TZenrekiDataTable,
  TKeirekiDataTable,
  TSikakuDataTable,
  THyosyorekiDataTable,
  TSidoTyokairekiDataTable,
  TMenkyoKosinRirekiDataTable,
  TZikorekiDataTable,
  TUntenKirokuSyomeiDataTable,
  TClaimrekiDataTable,
  TAlcoholDataTable,
  initialAlcoholDataTable,
  initialClaimrekiDataTable,
  initialHyosyorekiDataTable,
  initialKeirekiDataTable,
  initialMenkyoKosinRirekiDataTable,
  initialSidoTyokairekiDataTable,
  initialSikakuDataTable,
  initialUntenKirokuSyomeiDataTable,
  initialZenrekiDataTable,
  initialZikorekiDataTable,
  TZenrekiData,
  TKeirekiData,
  THyosyorekiData,
  TSikakuData,
  TSidoTyokairekiData,
  TMenkyoKosinRirekiData,
  TZikorekiData,
  TUntenKirokuSyomeiData,
  TClaimrekiData,
  TAlcoholData,
  TMenkyoFileDataTable,
  initialMenkyoFileDataTable,
  TMenkyoFileData,
} from "./kakusyuRirekiData"
import {
  TKenkoSindanKirokuDataTable,
  TAnzenKaigiKirokuDataTable,
  TTekiseiSindanKirokuDataTable,
  TAnzenKaigiKirokuData,
  TKenkoSindanKirokuData,
  TTekiseiSindanKirokuData,
  initialKenkoSindanKirokuDataTable,
  initialAnzenKaigiKirokuDataTable,
  initialTekiseiSindanKirokuDataTable,
} from "./kyoikuKensyuJusinData"
import dayjs from "dayjs"
import dateUtil from "../../../ts/util/dateUtil"

/**
 * Immer の draft を実体（プレーンオブジェクト）に変換する。
 *
 * draft のまま Object.freeze すると、ネストしたオブジェクト（添付ファイル等）が
 * 子 draft のまま凍結される。Immer は凍結済みオブジェクトの finalize をスキップするため、
 * その子 draft は producer 終了時に revoke され、state に失効プロキシが残る。
 * 結果、serializableStateInvariantMiddleware が
 * 「Cannot perform 'IsArray' on a proxy that has been revoked」で落ちる。
 */
const toPlainItem = <T,>(item: T): T => (isDraft(item) ? (current(item) as T) : item)

/**
 * 乗務員マスタ型定義
 */
export type TZyomuinMasterState = Partial<{
  // 共通
  乗務員ID: number
  社員ID: number
  社員コード: string
  社員氏名: string
  社員氏名カナ: string
  部所ID?: number
  部所コード: string
  部所名: string
  部所略称: string
  // 紐づく車番の一覧
  車番: string
  削除フラグ?: number
  作成システム区分?: number
  作成ユーザID?: number
  作成日時?: string
  更新システム区分?: number
  更新ユーザID?: number
  更新日時?: string

  // modalの動作用
  modal表彰日: string | null
}> &
  // 基本データ
  TZyomuinMasterBase &
  TMenkyoJoho &
  // 教育・研修・受診
  TKenkoSindanKirokuDataTable &
  TAnzenKaigiKirokuDataTable &
  TTekiseiSindanKirokuDataTable &
  // 各種履歴
  TZenrekiDataTable &
  TKeirekiDataTable &
  TSikakuDataTable &
  THyosyorekiDataTable &
  TSidoTyokairekiDataTable &
  TMenkyoKosinRirekiDataTable &
  TMenkyoFileDataTable &
  TZikorekiDataTable &
  TUntenKirokuSyomeiDataTable &
  TClaimrekiDataTable &
  TAlcoholDataTable &
  // 部所異動履歴
  TBsyoIdoRirekiDataTable

export const getZyomuin = createAsyncThunk<TZyomuinGetRes, number, {}>(
  "zyomuinMaster/getZyomuin",
  async (id: number) => {
    try {
      const res = await new ZyomuinService().get(id)
      return res.data
    } catch (error) {
      throw error
    }
  }
)

/**
 * 乗務員マスタ初期値
 */
export const initialState: TZyomuinMasterState = {
  乗務員ID: 0,
  社員ID: 0,
  社員コード: "",
  社員氏名: "",
  社員氏名カナ: "",
  部所ID: undefined,
  部所コード: "",
  部所名: "",
  部所略称: "",
  // 紐づく車番の一覧
  車番: "",
  削除フラグ: undefined,
  作成システム区分: undefined,
  作成ユーザID: undefined,
  作成日時: undefined,
  更新システム区分: undefined,
  更新ユーザID: undefined,
  更新日時: undefined,

  // 基本データ
  ...initialSyakenSyoState,
  ...initialMenkyoJohoState,
  // 教育・研修・受診
  ...initialKenkoSindanKirokuDataTable,
  ...initialAnzenKaigiKirokuDataTable,
  ...initialTekiseiSindanKirokuDataTable,
  // 各種履歴
  ...initialZenrekiDataTable,
  ...initialKeirekiDataTable,
  ...initialSikakuDataTable,
  ...initialHyosyorekiDataTable,
  ...initialSidoTyokairekiDataTable,
  ...initialMenkyoKosinRirekiDataTable,
  ...initialMenkyoFileDataTable,
  ...initialZikorekiDataTable,
  ...initialUntenKirokuSyomeiDataTable,
  ...initialClaimrekiDataTable,
  ...initialAlcoholDataTable,
  // 部所異動履歴
  ...initialBsyoIdoRirekiDataTable,
}

export const zyomuinMasterSlice = createSlice({
  name: "zyomuinMaster",
  initialState,
  reducers: {
    init: () => {
      return initialState
    },
    set: (state, action: PayloadAction<TZyomuinMasterState>) => {
      return { ...state, ...action.payload }
    },
    // 共通
    setModal表彰日: (state, action: PayloadAction<TZyomuinMasterState["modal表彰日"]>) => {
      return { ...state, modal表彰日: action.payload }
    },
    set社員番号: (state, action: PayloadAction<TZyomuinMasterState["社員コード"]>) => {
      return { ...state, 社員番号: action.payload }
    },
    set社員氏名: (state, action: PayloadAction<TZyomuinMasterState["社員氏名"]>) => {
      return { ...state, 社員氏名: action.payload }
    },
    set社員氏名カナ: (state, action: PayloadAction<TZyomuinMasterState["社員氏名カナ"]>) => {
      return { ...state, 社員氏名カナ: action.payload }
    },
    set部所名: (state, action: PayloadAction<TZyomuinMasterState["部所名"]>) => {
      return { ...state, 部所名: action.payload }
    },
    // 基本データ
    set選任日: (state, action: PayloadAction<TZyomuinMasterState["選任日"]>) => {
      return { ...state, 選任日: action.payload }
    },
    set選任種類: (state, action: PayloadAction<TZyomuinMasterState["選任種類"]>) => {
      return { ...state, 選任種類: action.payload }
    },
    set携帯アドレス: (state, action: PayloadAction<TZyomuinMasterState["携帯アドレス"]>) => {
      return { ...state, 携帯アドレス: action.payload }
    },
    set緊急連絡先: (state, action: PayloadAction<TZyomuinMasterState["緊急連絡先"]>) => {
      return { ...state, 緊急連絡先: action.payload }
    },
    set緊急連絡先電話番号: (state, action: PayloadAction<TZyomuinMasterState["緊急連絡先番号"]>) => {
      return { ...state, 緊急連絡先番号: action.payload }
    },
    // 顔写真関係
    set顔写真: (state, action: PayloadAction<TPictureFile>) => {
      return { ...state, 顔写真: action.payload }
    },
    edit顔写真: (state, action: PayloadAction<TPictureFile>) => {
      return {
        ...state,
        顔写真: action.payload,
      }
    },
    remove顔写真: (state, action: PayloadAction<TPictureFile>) => {
      return {
        ...state,
        顔写真: undefined,
      }
    },
    set免許取得日: (state, action: PayloadAction<TZyomuinMasterState["免許取得日"]>) => {
      return { ...state, 免許取得日: action.payload }
    },
    set免許証番号: (state, action: PayloadAction<TZyomuinMasterState["免許証番号"]>) => {
      return { ...state, 免許証番号: action.payload }
    },
    set免許大型フラグ: (state, action: PayloadAction<TZyomuinMasterState["免許大型フラグ"]>) => {
      return { ...state, 免許大型フラグ: action.payload }
    },
    set免許中型フラグ: (state, action: PayloadAction<TZyomuinMasterState["免許中型フラグ"]>) => {
      return { ...state, 免許中型フラグ: action.payload }
    },
    set免許普通フラグ: (state, action: PayloadAction<TZyomuinMasterState["免許普通フラグ"]>) => {
      return { ...state, 免許普通フラグ: action.payload }
    },
    set免許大特フラグ: (state, action: PayloadAction<TZyomuinMasterState["免許大特フラグ"]>) => {
      return { ...state, 免許大特フラグ: action.payload }
    },
    set免許大自二フラグ: (state, action: PayloadAction<TZyomuinMasterState["免許大自二フラグ"]>) => {
      return { ...state, 免許大自二フラグ: action.payload }
    },
    set免許普自二フラグ: (state, action: PayloadAction<TZyomuinMasterState["免許普自二フラグ"]>) => {
      return { ...state, 免許普自二フラグ: action.payload }
    },
    set免許小特フラグ: (state, action: PayloadAction<TZyomuinMasterState["免許小特フラグ"]>) => {
      return { ...state, 免許小特フラグ: action.payload }
    },
    set免許原付フラグ: (state, action: PayloadAction<TZyomuinMasterState["免許原付フラグ"]>) => {
      return { ...state, 免許原付フラグ: action.payload }
    },
    set免許け引フラグ: (state, action: PayloadAction<TZyomuinMasterState["免許け引フラグ"]>) => {
      return { ...state, 免許け引フラグ: action.payload }
    },
    set免許大二フラグ: (state, action: PayloadAction<TZyomuinMasterState["免許大二フラグ"]>) => {
      return { ...state, 免許大二フラグ: action.payload }
    },
    set免許中二フラグ: (state, action: PayloadAction<TZyomuinMasterState["免許中二フラグ"]>) => {
      return { ...state, 免許中二フラグ: action.payload }
    },
    set免許普二フラグ: (state, action: PayloadAction<TZyomuinMasterState["免許普二フラグ"]>) => {
      return { ...state, 免許普二フラグ: action.payload }
    },
    set免許大特二フラグ: (state, action: PayloadAction<TZyomuinMasterState["免許大特二フラグ"]>) => {
      return { ...state, 免許大特二フラグ: action.payload }
    },
    set免許け引二フラグ: (state, action: PayloadAction<TZyomuinMasterState["免許け引二フラグ"]>) => {
      return { ...state, 免許け引二フラグ: action.payload }
    },
    set免許準中型フラグ: (state, action: PayloadAction<TZyomuinMasterState["免許準中型フラグ"]>) => {
      return { ...state, 免許準中型フラグ: action.payload }
    },
    set免許条件1: (state, action: PayloadAction<TZyomuinMasterState["免許条件1"]>) => {
      return { ...state, 免許条件1: action.payload }
    },
    set免許条件2: (state, action: PayloadAction<TZyomuinMasterState["免許条件2"]>) => {
      return { ...state, 免許条件2: action.payload }
    },
    set免許条件3: (state, action: PayloadAction<TZyomuinMasterState["免許条件3"]>) => {
      return { ...state, 免許条件3: action.payload }
    },
    set運転記録最終取得日: (state, action: PayloadAction<TZyomuinMasterState["運転記録最終取得日"]>) => {
      return { ...state, 運転記録最終取得日: action.payload }
    },
    setSDカード色: (state, action: PayloadAction<TZyomuinMasterState["SDカード色"]>) => {
      return { ...state, SDカード色: action.payload }
    },
    set無事故年数: (state, action: PayloadAction<TZyomuinMasterState["無事故年数"]>) => {
      return { ...state, 無事故年数: action.payload }
    },
    // 教育・研修・受診
    set健康診断記録テーブル: (state, action: PayloadAction<TKenkoSindanKirokuData | TKenkoSindanKirokuData[]>) => {
      if (Array.isArray(action.payload)) {
        return { ...state, 健康診断記録データリスト: [...action.payload] }
      }
      return { ...state, 健康診断記録データリスト: [action.payload] }
    },
    edit健康診断記録テーブル: (state, action: PayloadAction<TKenkoSindanKirokuData>) => {
      // 変更前データリストから変更行のrow_idと一致するデータ行を抽出。
      let items = state.健康診断記録データリスト
      let rowId = action.payload.row_id
      let index = items.findIndex((i) => i.row_id === rowId)
      let oldItem = items[index]

      // エラー回避用
      if (rowId == null || index < 0) return state

      // 編集データをコピーする。
      let clonedItem = Object.freeze(copyObject({}, toPlainItem(oldItem), action.payload))

      // データリストに編集データを差し込む。
      return copyObject({}, state, {
        健康診断記録データリスト: items
          .slice(0, index)
          .concat([clonedItem])
          .concat(items.slice(index + 1)),
      })
    },
    set安全会議記録テーブル: (state, action: PayloadAction<TAnzenKaigiKirokuData | TAnzenKaigiKirokuData[]>) => {
      if (Array.isArray(action.payload)) {
        return { ...state, 安全会議記録データリスト: [...action.payload] }
      }
      return { ...state, 安全会議記録データリスト: [action.payload] }
    },

    set適性診断記録テーブル: (state, action: PayloadAction<TTekiseiSindanKirokuData | TTekiseiSindanKirokuData[]>) => {
      if (Array.isArray(action.payload)) {
        return { ...state, 適正診断記録データリスト: [...action.payload] }
      }
      return { ...state, 適正診断記録データリスト: [action.payload] }
    },

    // 各種履歴

    // #region [color2] 前歴
    set前歴テーブル: (state, action: PayloadAction<TZenrekiData | TZenrekiData[]>) => {
      if (Array.isArray(action.payload)) {
        return { ...state, 前歴データリスト: [...action.payload] }
      }
      return { ...state, 前歴データリスト: [action.payload] }
    },

    add前歴テーブル: (state, action: PayloadAction<TZenrekiData>) => {
      let newItem = Object.freeze(copyObject({}, { ...action.payload, row_id: commonUtil.getUniqueStr() }))
      return copyObject({}, state, {
        前歴データリスト: state.前歴データリスト.concat([newItem]),
      })
    },
    edit前歴テーブル: (state, action: PayloadAction<TZenrekiData>) => {
      // 変更前データリストから変更行のrow_idと一致するデータ行を抽出。
      let items = state.前歴データリスト
      let rowId = action.payload.row_id
      let index = items.findIndex((i) => i.row_id === rowId)
      let oldItem = items[index]

      // エラー回避用
      if (rowId == null || index < 0) return state

      // 編集データをコピーする。
      let clonedItem = Object.freeze(copyObject({}, toPlainItem(oldItem), action.payload))

      // データリストに編集データを差し込む。
      return copyObject({}, state, {
        前歴データリスト: items
          .slice(0, index)
          .concat([clonedItem])
          .concat(items.slice(index + 1)),
      })
    },
    remove前歴テーブル: (state, action: PayloadAction<{ row_id: string }>) => {
      let items = state.前歴データリスト,
        row_id = action.payload.row_id
      const index = items.findIndex((i) => i.row_id === row_id)

      if (row_id == null || index < 0) return state
      return copyObject({}, state, {
        前歴データリスト: items.slice(0, index).concat(items.slice(index + 1)),
      })
    },
    //#endregion

    // #region [color2] 経歴
    set経歴テーブル: (state, action: PayloadAction<TKeirekiData | TKeirekiData[]>) => {
      if (Array.isArray(action.payload)) {
        return { ...state, 経歴データリスト: [...action.payload] }
      }
      return { ...state, 経歴データリスト: [action.payload] }
    },
    add経歴テーブル: (state, action: PayloadAction<TKeirekiData>) => {
      let newItem = Object.freeze(copyObject({}, { ...action.payload, row_id: commonUtil.getUniqueStr() }))
      return copyObject({}, state, {
        経歴データリスト: state.経歴データリスト.concat([newItem]),
      })
    },
    edit経歴テーブル: (state, action: PayloadAction<TKeirekiData>) => {
      // 変更前データリストから変更行のrow_idと一致するデータ行を抽出。
      let items = state.経歴データリスト
      let rowId = action.payload.row_id
      let index = items.findIndex((i) => i.row_id === rowId)
      let oldItem = items[index]

      // エラー回避用
      if (rowId == null || index < 0) return state

      // 編集データをコピーする。
      let clonedItem = Object.freeze(copyObject({}, toPlainItem(oldItem), action.payload))

      // データリストに編集データを差し込む。
      return copyObject({}, state, {
        経歴データリスト: items
          .slice(0, index)
          .concat([clonedItem])
          .concat(items.slice(index + 1)),
      })
    },
    remove経歴テーブル: (state, action: PayloadAction<{ row_id: string }>) => {
      let items = state.経歴データリスト,
        row_id = action.payload.row_id
      const index = items.findIndex((i) => i.row_id === row_id)

      if (row_id == null || index < 0) return state
      return copyObject({}, state, {
        経歴データリスト: items.slice(0, index).concat(items.slice(index + 1)),
      })
    },
    //#endregion

    // #region [color2] 資格
    set資格テーブル: (state, action: PayloadAction<TSikakuData | TSikakuData[]>) => {
      if (Array.isArray(action.payload)) {
        return { ...state, 資格データリスト: [...action.payload] }
      }
      return { ...state, 資格データリスト: [action.payload] }
    },
    add資格テーブル: (state, action: PayloadAction<TSikakuData | TSikakuData[]>) => {
      let newItem = Object.freeze(copyObject({}, { ...action.payload, row_id: commonUtil.getUniqueStr() }))
      return copyObject({}, state, {
        資格データリスト: state.資格データリスト.concat([newItem]),
      })
    },
    edit資格テーブル: (state, action: PayloadAction<TSikakuData>) => {
      // 変更前データリストから変更行のrow_idと一致するデータ行を抽出。
      let items = state.資格データリスト
      let rowId = action.payload.row_id
      let index = items.findIndex((i) => i.row_id === rowId)
      let oldItem = items[index]

      // エラー回避用
      if (rowId == null || index < 0) return state

      // 編集データをコピーする。
      let clonedItem = Object.freeze(copyObject({}, toPlainItem(oldItem), action.payload))

      // データリストに編集データを差し込む。
      return copyObject({}, state, {
        資格データリスト: items
          .slice(0, index)
          .concat([clonedItem])
          .concat(items.slice(index + 1)),
      })
    },
    remove資格テーブル: (state, action: PayloadAction<{ row_id: string }>) => {
      let items = state.資格データリスト,
        row_id = action.payload.row_id
      const index = items.findIndex((i) => i.row_id === row_id)

      if (row_id == null || index < 0) return state
      return copyObject({}, state, {
        資格データリスト: items.slice(0, index).concat(items.slice(index + 1)),
      })
    },
    //#endregion

    // #region [color2] 表彰歴
    set表彰歴テーブル: (state, action: PayloadAction<THyosyorekiData | THyosyorekiData[]>) => {
      if (Array.isArray(action.payload)) {
        return { ...state, 表彰歴データリスト: [...action.payload] }
      }
      return { ...state, 表彰歴データリスト: [action.payload] }
    },
    add表彰歴テーブル: (state, action: PayloadAction<THyosyorekiData | THyosyorekiData[]>) => {
      let newItem = Object.freeze(copyObject({}, { ...action.payload, row_id: commonUtil.getUniqueStr() }))
      return copyObject({}, state, {
        表彰歴データリスト: state.表彰歴データリスト.concat([newItem]),
      })
    },
    edit表彰歴テーブル: (state, action: PayloadAction<THyosyorekiData>) => {
      // 変更前データリストから変更行のrow_idと一致するデータ行を抽出。
      let items = state.表彰歴データリスト
      let rowId = action.payload.row_id
      let index = items.findIndex((i) => i.row_id === rowId)
      let oldItem = items[index]

      // エラー回避用
      if (rowId == null || index < 0) return state

      // 編集データをコピーする。
      let clonedItem = Object.freeze(copyObject({}, toPlainItem(oldItem), action.payload))

      // データリストに編集データを差し込む。
      return copyObject({}, state, {
        表彰歴データリスト: items
          .slice(0, index)
          .concat([clonedItem])
          .concat(items.slice(index + 1)),
      })
    },
    remove表彰歴テーブル: (state, action: PayloadAction<{ row_id: string }>) => {
      let items = state.表彰歴データリスト,
        row_id = action.payload.row_id
      const index = items.findIndex((i) => i.row_id === row_id)

      if (row_id == null || index < 0) return state
      return copyObject({}, state, {
        表彰歴データリスト: items.slice(0, index).concat(items.slice(index + 1)),
      })
    },
    //#endregion

    // #region [color2] 表彰歴
    set指導懲戒歴テーブル: (state, action: PayloadAction<TSidoTyokairekiData | TSidoTyokairekiData[]>) => {
      if (Array.isArray(action.payload)) {
        return { ...state, 指導懲戒歴データリスト: [...action.payload] }
      }
      return { ...state, 指導懲戒歴データリスト: [action.payload] }
    },
    add指導懲戒歴テーブル: (state, action: PayloadAction<TSidoTyokairekiData | TSidoTyokairekiData[]>) => {
      let newItem = Object.freeze(copyObject({}, { ...action.payload, row_id: commonUtil.getUniqueStr() }))
      return copyObject({}, state, {
        指導懲戒歴データリスト: state.指導懲戒歴データリスト.concat([newItem]),
      })
    },
    edit指導懲戒歴テーブル: (state, action: PayloadAction<TSidoTyokairekiData>) => {
      // 変更前データリストから変更行のrow_idと一致するデータ行を抽出。
      let items = state.指導懲戒歴データリスト
      let rowId = action.payload.row_id
      let index = items.findIndex((i) => i.row_id === rowId)
      let oldItem = items[index]

      // エラー回避用
      if (rowId == null || index < 0) return state

      // 編集データをコピーする。
      let clonedItem = Object.freeze(copyObject({}, toPlainItem(oldItem), action.payload))

      // データリストに編集データを差し込む。
      return copyObject({}, state, {
        指導懲戒歴データリスト: items
          .slice(0, index)
          .concat([clonedItem])
          .concat(items.slice(index + 1)),
      })
    },
    remove指導懲戒歴テーブル: (state, action: PayloadAction<{ row_id: string }>) => {
      let items = state.指導懲戒歴データリスト,
        row_id = action.payload.row_id
      const index = items.findIndex((i) => i.row_id === row_id)

      if (row_id == null || index < 0) return state
      return copyObject({}, state, {
        指導懲戒歴データリスト: items.slice(0, index).concat(items.slice(index + 1)),
      })
    },
    //#endregion

    // #region [color2] 表彰歴
    set免許更新履歴テーブル: (state, action: PayloadAction<TMenkyoKosinRirekiData | TMenkyoKosinRirekiData[]>) => {
      if (Array.isArray(action.payload)) {
        return { ...state, 免許更新履歴データリスト: [...action.payload] }
      }
      return { ...state, 免許更新履歴データリスト: [action.payload] }
    },
    add免許更新履歴テーブル: (state, action: PayloadAction<TMenkyoKosinRirekiData | TMenkyoKosinRirekiData[]>) => {
      let newItem = Object.freeze(copyObject({}, { ...action.payload, row_id: commonUtil.getUniqueStr() }))
      return copyObject({}, state, {
        免許更新履歴データリスト: state.免許更新履歴データリスト.concat([newItem]),
      })
    },
    edit免許更新履歴テーブル: (state, action: PayloadAction<TMenkyoKosinRirekiData>) => {
      // 変更前データリストから変更行のrow_idと一致するデータ行を抽出。
      let items = state.免許更新履歴データリスト
      let rowId = action.payload.row_id
      let index = items.findIndex((i) => i.row_id === rowId)
      let oldItem = items[index]

      // エラー回避用
      if (rowId == null || index < 0) return state

      // 編集データをコピーする。
      let clonedItem = Object.freeze(copyObject({}, toPlainItem(oldItem), action.payload))

      // データリストに編集データを差し込む。
      return copyObject({}, state, {
        免許更新履歴データリスト: items
          .slice(0, index)
          .concat([clonedItem])
          .concat(items.slice(index + 1)),
      })
    },
    remove免許更新履歴テーブル: (state, action: PayloadAction<{ row_id: string }>) => {
      let items = state.免許更新履歴データリスト,
        row_id = action.payload.row_id
      const index = items.findIndex((i) => i.row_id === row_id)

      if (row_id == null || index < 0) return state
      return copyObject({}, state, {
        免許更新履歴データリスト: items.slice(0, index).concat(items.slice(index + 1)),
      })
    },
    //#endregion

    //#region [color2] 免許ファイル一覧テーブル
    add免許ファイル一覧テーブル: (state, action: PayloadAction<TMenkyoFileData>) => {
      let newItem = Object.freeze(
        copyObject(
          {},
          {
            ...action.payload,
            row_id: action.payload.row_id ?? commonUtil.getUniqueStr(),
          }
        )
      )
      return copyObject({}, state, {
        免許ファイルリスト: state.免許ファイルリスト.concat([newItem]),
      })
    },
    edit免許ファイル一覧テーブル: (state, action: PayloadAction<TMenkyoFileData>) => {
      // 変更前データリストから変更行のrow_idと一致するデータ行を抽出。
      let items = state.免許ファイルリスト
      let rowId = action.payload.row_id
      let index = items.findIndex((i) => i.row_id === rowId)
      let oldItem = items[index]

      // エラー回避用
      if (rowId == null || index < 0) return state

      // 編集データをコピーする。
      let clonedItem = Object.freeze(copyObject({}, toPlainItem(oldItem), action.payload))

      // データリストに編集データを差し込む。
      return copyObject({}, state, {
        免許ファイルリスト: items
          .slice(0, index)
          .concat([clonedItem])
          .concat(items.slice(index + 1)),
      })
    },
    remove免許ファイル一覧テーブル: (state, action: PayloadAction<{ row_id: string }>) => {
      let items = state.免許ファイルリスト,
        row_id = action.payload.row_id
      const index = items.findIndex((i) => i.row_id === row_id)

      if (row_id == null || index < 0) return state
      return copyObject({}, state, {
        免許ファイルリスト: items.slice(0, index).concat(items.slice(index + 1)),
      })
    },
    //#endregion

    //#region [color2] 事故歴
    add事故歴テーブル: (state, action: PayloadAction<TZikorekiData | TZikorekiData[]>) => {
      let newItem = Object.freeze(copyObject({}, { ...action.payload, row_id: commonUtil.getUniqueStr() }))
      return copyObject({}, state, {
        事故歴データリスト: state.事故歴データリスト.concat([newItem]),
      })
    },
    edit事故歴テーブル: (state, action: PayloadAction<TZikorekiData>) => {
      // 変更前データリストから変更行のrow_idと一致するデータ行を抽出。
      let items = state.事故歴データリスト
      let rowId = action.payload.row_id
      let index = items.findIndex((i) => i.row_id === rowId)
      let oldItem = items[index]

      // エラー回避用
      if (rowId == null || index < 0) return state

      // 編集データをコピーする。
      let clonedItem = Object.freeze(copyObject({}, toPlainItem(oldItem), action.payload))

      // データリストに編集データを差し込む。
      return copyObject({}, state, {
        事故歴データリスト: items
          .slice(0, index)
          .concat([clonedItem])
          .concat(items.slice(index + 1)),
      })
    },
    remove事故歴テーブル: (state, action: PayloadAction<{ row_id: string }>) => {
      let items = state.事故歴データリスト,
        row_id = action.payload.row_id
      const index = items.findIndex((i) => i.row_id === row_id)

      if (row_id == null || index < 0) return state
      return copyObject({}, state, {
        事故歴データリスト: items.slice(0, index).concat(items.slice(index + 1)),
      })
    },
    //#endregion

    //#region [color2] 運転記録証明
    set運転記録証明テーブル: (state, action: PayloadAction<TUntenKirokuSyomeiData | TUntenKirokuSyomeiData[]>) => {
      if (Array.isArray(action.payload)) {
        return { ...state, 運転記録証明データリスト: [...action.payload] }
      }
      return { ...state, 運転記録証明データリスト: [action.payload] }
    },
    add運転記録証明テーブル: (state, action: PayloadAction<TUntenKirokuSyomeiData | TUntenKirokuSyomeiData[]>) => {
      let newItem = Object.freeze(copyObject({}, { ...action.payload, row_id: commonUtil.getUniqueStr() }))
      return copyObject({}, state, {
        運転記録証明データリスト: state.運転記録証明データリスト.concat([newItem]),
      })
    },
    edit運転記録証明テーブル: (state, action: PayloadAction<TUntenKirokuSyomeiData>) => {
      // 変更前データリストから変更行のrow_idと一致するデータ行を抽出。
      let items = state.運転記録証明データリスト
      let rowId = action.payload.row_id
      let index = items.findIndex((i) => i.row_id === rowId)
      let oldItem = items[index]

      // エラー回避用
      if (rowId == null || index < 0) return state

      // 編集データをコピーする。
      let clonedItem = Object.freeze(copyObject({}, toPlainItem(oldItem), action.payload))

      // データリストに編集データを差し込む。
      return copyObject({}, state, {
        運転記録証明データリスト: items
          .slice(0, index)
          .concat([clonedItem])
          .concat(items.slice(index + 1)),
      })
    },
    remove運転記録証明テーブル: (state, action: PayloadAction<{ row_id: string }>) => {
      let items = state.運転記録証明データリスト,
        row_id = action.payload.row_id
      const index = items.findIndex((i) => i.row_id === row_id)

      if (row_id == null || index < 0) return state
      return copyObject({}, state, {
        運転記録証明データリスト: items.slice(0, index).concat(items.slice(index + 1)),
      })
    },
    //#endregion

    //#region [color2] クレーム歴
    addクレーム歴テーブル: (state, action: PayloadAction<TClaimrekiData | TClaimrekiData[]>) => {
      let newItem = Object.freeze(copyObject({}, { ...action.payload, row_id: commonUtil.getUniqueStr() }))
      return copyObject({}, state, {
        クレーム歴データリスト: state.クレーム歴データリスト.concat([newItem]),
      })
    },
    editクレーム歴テーブル: (state, action: PayloadAction<TClaimrekiData>) => {
      // 変更前データリストから変更行のrow_idと一致するデータ行を抽出。
      let items = state.クレーム歴データリスト
      let rowId = action.payload.row_id
      let index = items.findIndex((i) => i.row_id === rowId)
      let oldItem = items[index]

      // エラー回避用
      if (rowId == null || index < 0) return state

      // 編集データをコピーする。
      let clonedItem = Object.freeze(copyObject({}, toPlainItem(oldItem), action.payload))

      // データリストに編集データを差し込む。
      return copyObject({}, state, {
        クレーム歴データリスト: items
          .slice(0, index)
          .concat([clonedItem])
          .concat(items.slice(index + 1)),
      })
    },
    removeクレーム歴テーブル: (state, action: PayloadAction<{ row_id: string }>) => {
      let items = state.クレーム歴データリスト,
        row_id = action.payload.row_id
      const index = items.findIndex((i) => i.row_id === row_id)

      if (row_id == null || index < 0) return state
      return copyObject({}, state, {
        クレーム歴データリスト: items.slice(0, index).concat(items.slice(index + 1)),
      })
    },
    //#endregion

    //#region [color2] アルコール反応
    setアルコール反応テーブル: (state, action: PayloadAction<TAlcoholData | TAlcoholData[]>) => {
      if (Array.isArray(action.payload)) {
        return { ...state, アルコール反応データリスト: [...action.payload] }
      }
      return { ...state, アルコール反応データリスト: [action.payload] }
    },
    addアルコール反応テーブル: (state, action: PayloadAction<TAlcoholData | TAlcoholData[]>) => {
      let newItem = Object.freeze(copyObject({}, { ...action.payload, row_id: commonUtil.getUniqueStr() }))
      return copyObject({}, state, {
        アルコール反応データリスト: state.アルコール反応データリスト.concat([newItem]),
      })
    },
    editアルコール反応テーブル: (state, action: PayloadAction<TAlcoholData>) => {
      // 変更前データリストから変更行のrow_idと一致するデータ行を抽出。
      let items = state.アルコール反応データリスト
      let rowId = action.payload.row_id
      let index = items.findIndex((i) => i.row_id === rowId)
      let oldItem = items[index]

      // エラー回避用
      if (rowId == null || index < 0) return state

      // 編集データをコピーする。
      let clonedItem = Object.freeze(copyObject({}, toPlainItem(oldItem), action.payload))

      // データリストに編集データを差し込む。
      return copyObject({}, state, {
        アルコール反応データリスト: items
          .slice(0, index)
          .concat([clonedItem])
          .concat(items.slice(index + 1)),
      })
    },
    removeアルコール反応テーブル: (state, action: PayloadAction<{ row_id: string }>) => {
      let items = state.アルコール反応データリスト,
        row_id = action.payload.row_id
      const index = items.findIndex((i) => i.row_id === row_id)

      if (row_id == null || index < 0) return state
      return copyObject({}, state, {
        アルコール反応データリスト: items.slice(0, index).concat(items.slice(index + 1)),
      })
    },
    //#endregion

    // 部所異動履歴
    set部所異動履歴テーブル: (state, action: PayloadAction<TBsyoIdoRirekiData | TBsyoIdoRirekiData[]>) => {
      if (Array.isArray(action.payload)) {
        return { ...state, 部所異動履歴データリスト: [...action.payload] }
      }
      return { ...state, 部所異動履歴データリスト: [action.payload] }
    },
    add部所異動履歴テーブル: (state, action: PayloadAction<TBsyoIdoRirekiData | TBsyoIdoRirekiData[]>) => {
      if (Array.isArray(action.payload)) {
        return {
          ...state,
          部所異動履歴データリスト: [...state.部所異動履歴データリスト, ...action.payload],
        }
      }
      return {
        ...state,
        部所異動履歴データリスト: [...state.部所異動履歴データリスト, action.payload],
      }
    },
    remove部所異動履歴テーブル: (state, action: PayloadAction<{ row_id: string }>) => {
      let items = state.部所異動履歴データリスト,
        row_id = action.payload.row_id
      const index = items.findIndex((i) => i.row_id === row_id)

      if (row_id == null || index < 0) return state
      return copyObject({}, state, {
        部所異動履歴データリスト: items.slice(0, index).concat(items.slice(index + 1)),
      })
    },
    search: (state, action: PayloadAction<TZyomuinMasterState["社員コード"]>) => {
      // // TODO: APIから社員番号を条件に取得？
      // let zyomuinData = dummyZyomuinData.find(
      //   (d) => state.社員番号 === d.社員番号
      // )
      // if (zyomuinData == null) {
      //   return { ...state }
      // } else {
      //   return { ...state, ...zyomuinData }
      // }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getZyomuin.fulfilled, (state: TZyomuinMasterState, action) => {
      // 共通
      state.乗務員ID = action.payload.乗務員ID
      state.社員ID = action.payload.社員ID
      state.社員コード = action.payload.社員コード
      state.社員氏名 = action.payload.社員氏名
      state.社員氏名カナ = action.payload.社員氏名カナ
      state.部所ID = action.payload.部所ID
      state.部所コード = action.payload.部所コード
      state.部所名 = action.payload.部所名
      state.部所略称 = action.payload.部所略称
      state.車番 = action.payload.車番
      state.削除フラグ = action.payload.削除フラグ
      state.作成システム区分 = action.payload.作成システム区分
      state.作成ユーザID = action.payload.作成ユーザID
      state.作成日時 = action.payload.作成日時
      state.更新システム区分 = action.payload.更新システム区分
      state.更新ユーザID = action.payload.更新ユーザID
      state.更新日時 = action.payload.更新日時

      // ZyomuinMasterBase
      state.入社年月日 = action.payload.入社年月日
      state.生年月日 = action.payload.生年月日
      state.性別区分ID = action.payload.性別区分ID
      state.性別区分コード = action.payload.性別区分コード
      state.性別区分名称1 = action.payload.性別区分名称1
      state.性別区分ソート番号 = action.payload.性別区分ソート番号
      state.血液型区分ID = action.payload.血液型区分ID
      state.血液型区分コード = action.payload.血液型区分コード
      state.血液型区分名称1 = action.payload.血液型区分名称1
      state.血液型区分ソート番号 = action.payload.血液型区分ソート番号

      state.選任日 = action.payload.選任日
      state.選任種類 = action.payload.選任種類

      state.郵便番号 = action.payload.郵便番号
      state.住所 = action.payload.住所
      state.電話番号 = action.payload.電話番号
      state.携帯電話番号 = action.payload.携帯電話番号
      state.メールアドレス = action.payload.メールアドレス

      state.緊急連絡先 = action.payload.緊急連絡先
      state.緊急連絡先番号 = action.payload.緊急連絡先番号
      state.携帯アドレス = action.payload.携帯アドレス

      state.社員カードID = action.payload.社員カードID
      state.入社区分ID = action.payload.入社区分ID
      state.入社区分コード = action.payload.入社区分コード
      state.入社区分名称1 = action.payload.入社区分名称1
      state.入社区分ソート番号 = action.payload.入社区分ソート番号
      state.退職年月日 = action.payload.退職年月日
      state.退職フラグ = action.payload.退職フラグ ?? 0
      state.退職事由 = action.payload.退職事由
      state.備考 = action.payload.備考

      // TMenkyoJoho
      state.免許取得日 = action.payload.免許取得日
      state.免許証番号 = action.payload.免許証番号
      state.免許大型フラグ = action.payload.免許大型フラグ
      state.免許中型フラグ = action.payload.免許中型フラグ
      state.免許普通フラグ = action.payload.免許普通フラグ
      state.免許大特フラグ = action.payload.免許大特フラグ
      state.免許大自二フラグ = action.payload.免許大自二フラグ
      state.免許普自二フラグ = action.payload.免許普自二フラグ
      state.免許小特フラグ = action.payload.免許小特フラグ
      state.免許原付フラグ = action.payload.免許原付フラグ
      state.免許け引フラグ = action.payload.免許け引フラグ
      state.免許大二フラグ = action.payload.免許大二フラグ
      state.免許中二フラグ = action.payload.免許中二フラグ
      state.免許普二フラグ = action.payload.免許普二フラグ
      state.免許大特二フラグ = action.payload.免許大特二フラグ
      state.免許け引二フラグ = action.payload.免許け引二フラグ
      state.免許準中型フラグ = action.payload.免許準中型フラグ
      state.免許条件1 = action.payload.免許条件1
      state.免許条件2 = action.payload.免許条件2
      state.免許条件3 = action.payload.免許条件3

      state.運転記録最終取得日 = action.payload.運転記録最終取得日
      state.SDカード色 = action.payload.SDカード色
      state.無事故年数 = action.payload.無事故年数
      state.その他手当 = action.payload.その他手当

      if ((action.payload.顔写真ファイルURL ?? "") !== "") {
        state.Has顔写真AtStart = true
        state.顔写真 = {
          row_id: commonUtil.getUniqueStr(),
          isNewFile: false,
          ファイルURL: action.payload.顔写真ファイルURL,
          ファイル名: action.payload.顔写真ファイル名,
        }
      } else {
        state.Has顔写真AtStart = false
        state.顔写真 = undefined
      }

      state.安全会議記録データリスト = commonUtil.addRowId(action.payload.安全管理実施リスト)
      state.健康診断記録データリスト = commonUtil.addRowId(action.payload.健康診断実施リスト).map((d) => {
        const has添付 = (d.添付ファイルURL ?? "") !== ""
        return {
          ...d,
          row_id: commonUtil.getUniqueStr(),
          Has添付ファイルAtStart: has添付,
          添付ファイル: has添付
            ? {
                isNewFile: false,
                ファイルURL: d.添付ファイルURL,
                ファイル名: d.添付ファイル名,
              }
            : undefined,
        }
      })
      state.適正診断記録データリスト = commonUtil.addRowId(action.payload.適性診断リスト)

      // 乗務員マスタの履歴系のグリッドは日付入力で和暦に対応するため、文字列のYYYYMMDDに変更する
      state.前歴データリスト = commonUtil.addRowId(action.payload.前歴リスト).map((d) => ({
        ...d,
        input入学入社日: dateUtil.format(d.入学入社日, "YYYYMMDD"),
        input卒業退社日: dateUtil.format(d.卒業退社日, "YYYYMMDD"),
      }))

      state.経歴データリスト = commonUtil.addRowId(action.payload.経歴リスト)

      state.資格データリスト = commonUtil.addRowId(action.payload.資格リスト).map((d) => ({
        ...d,
        input資格取得日: dateUtil.format(d.資格取得日, "YYYYMMDD"),
      }))

      state.表彰歴データリスト = commonUtil.addRowId(action.payload.表彰歴リスト).map((d) => {
        const has添付 = (d.添付ファイルURL ?? "") !== ""
        return {
          ...d,
          row_id: commonUtil.getUniqueStr(),
          input表彰日: dateUtil.format(d.表彰日, "YYYYMMDD"),
          Has添付ファイルAtStart: has添付,
          添付ファイル: has添付
            ? {
                isNewFile: false,
                ファイルURL: d.添付ファイルURL,
                ファイル名: d.添付ファイル名,
              }
            : undefined,
        }
      })

      state.指導懲戒歴データリスト = commonUtil.addRowId(action.payload.指導歴リスト).map((d) => {
        const has添付 = (d.添付ファイルURL ?? "") !== ""
        return {
          ...d,
          row_id: commonUtil.getUniqueStr(),
          input指導懲戒日: dateUtil.format(d.指導懲戒日, "YYYYMMDD"),
          Has添付ファイルAtStart: has添付,
          添付ファイル: has添付
            ? {
                isNewFile: false,
                ファイルURL: d.添付ファイルURL,
                ファイル名: d.添付ファイル名,
              }
            : undefined,
        }
      })

      state.免許更新履歴データリスト = commonUtil.addRowId(action.payload.免許更新リスト).map((d) => ({
        ...d,
        input免許更新日: dateUtil.format(d.免許更新日, "YYYYMMDD"),
        input有効期限: dateUtil.format(d.有効期限, "YYYYMMDD"),
        input面談日: dateUtil.format(d.面談日, "YYYYMMDD"),
      }))

      state.免許ファイルリスト = commonUtil.addRowId(action.payload.免許ファイルリスト).map((d) => {
        const has添付 = (d.ファイルURL ?? "") !== ""
        return {
          ...d,
          HasファイルAtStart: has添付,
          ファイル: has添付
            ? {
                isNewFile: false,
                ファイルURL: d.ファイルURL,
                ファイル名: d.ファイル名,
              }
            : undefined,
        }
      })

      state.事故歴データリスト = action.payload.事故履歴リスト.map((item) => {
        const has添付 = (item.添付ファイルURL ?? "") !== ""
        return {
          ...item,
          row_id: commonUtil.getUniqueStr(),
          input発生日: dateUtil.format(item.発生日, "YYYYMMDD"),
          Has添付ファイルAtStart: has添付,
          添付ファイル: has添付
            ? {
                isNewFile: false,
                ファイルURL: item.添付ファイルURL,
                ファイル名: item.添付ファイル名,
              }
            : undefined,
        }
      })
      state.運転記録証明データリスト = commonUtil.addRowId(action.payload.運転記録リスト).map((d) => ({
        ...d,
        input違反日: dateUtil.format(d.違反日, "YYYYMMDD"),
      }))

      state.クレーム歴データリスト = action.payload.クレームリスト.map((item) => {
        const has添付 = (item.添付ファイルURL ?? "") !== ""
        return {
          ...item,
          row_id: commonUtil.getUniqueStr(),
          Has添付ファイルAtStart: has添付,
          添付ファイル: has添付
            ? {
                isNewFile: false,
                ファイルURL: item.添付ファイルURL,
                ファイル名: item.添付ファイル名,
              }
            : undefined,
        }
      })
      state.アルコール反応データリスト = commonUtil.addRowId(
        action.payload.アルコール反応リスト.map((item): TAlcoholData => {
          const casted = item.測定日時 != null && item.測定日時 ? dayjs(item.測定日時).toDate() : null
          return {
            ...item,
            // 編集型の都合で測定日時をDate型に変換したものを別途設定する
            測定日時_入力用: casted != null ? casted : undefined,
          }
        })
      )
      state.部所異動履歴データリスト = commonUtil.addRowId(action.payload.乗務員部所異動履歴リスト)
    })
  },
})

export const zyomuinMasterActions = zyomuinMasterSlice.actions
